import { computed, Injectable, signal } from '@angular/core';
import type { ConsensusRow } from '@api-response-types/consensus.response';
import {
  rowMatchesScope,
  type ReconciliationScope,
} from '@api-types/sse/reconciliation-scope';
import type { FilterValue } from '@api-types/types/filter-value.type';
import type { FilterModel } from '@libs/shared/validators/consensus.validators';
import { TreeExpansionController } from '@components/table/tree-expansion';
import { GridApi, IRowNode, RefreshCellsParams } from 'ag-grid-enterprise';
import {
  buildScopeMap,
  extractScopes,
  removeScope,
  type ScopeMap,
  upsertScope,
} from './consensus-grid-state.helper';
import {
  isConsensusGroupNode,
  isGrandTotalRoot,
} from './consensus-tree-expansion.helper';

const MAX_EXPAND_DEPTH = 50;

export type CompletedReconciliation = {
  scope: ReconciliationScope;
  consensusColumnId: string;
  dateValue: string;
};

export type RenderedValueReloader = (
  months: string[],
  scopes: readonly ReconciliationScope[],
) => Promise<boolean>;

@Injectable({
  providedIn: 'root',
})
export class ConsensusGridStateService {
  private _gridApi: GridApi | null = null;

  public readonly treeExpansion = new TreeExpansionController<ConsensusRow>({
    isGroupNode: isConsensusGroupNode,
    isProtectedRoot: isGrandTotalRoot,
    maxDepth: MAX_EXPAND_DEPTH,
    fetchGate: {
      activeFetchCount: () => this.activeFetches,
      whenIdle: () => this.whenServerSideIdle(),
    },
  });

  // when true, the consensus grid is rendered read-only (e.g. a finished cycle):
  // no create/edit/lock affordances, only history viewing.
  private readonly _readOnly = signal(false);
  public readonly readOnly = this._readOnly.asReadonly();

  public setReadOnly(value: boolean): void {
    this._readOnly.set(value);
  }

  private readonly _cycleWriteRejected = signal(false);
  public readonly cycleWriteRejected = this._cycleWriteRejected.asReadonly();

  public setCycleWriteRejected(value: boolean): void {
    this._cycleWriteRejected.set(value);
  }

  public readonly reconcilingScopesByColumnAndMonth = signal<ScopeMap>(
    new Map(),
  );

  public readonly pendingRefreshScopesByColumnAndMonth = signal<ScopeMap>(
    new Map(),
  );

  // AG Grid holds its filter model behind plain getters nothing can react to.
  // Mirrored into a signal here so the page can show its clear-filters
  // affordance and persist the model into the user's view.
  private readonly _columnFilters = signal<FilterModel>({});
  public readonly columnFilters = this._columnFilters.asReadonly();
  public readonly hasColumnFilters = computed(
    () => Object.keys(this._columnFilters()).length > 0,
  );

  /**
   * A saved model waiting for the grid to carry the columns it names. Set by
   * the page from the user's view; the table drains it once its month columns
   * exist, since a filter on a colId the grid does not know is dropped.
   */
  // `gridApi` is a plain field, so anything waiting for the grid needs a signal
  // to react to
  private readonly _gridReady = signal(false);
  public readonly gridReady = this._gridReady.asReadonly();

  private readonly _pendingColumnFilters = signal<FilterModel | null>(null);
  public readonly pendingColumnFilters =
    this._pendingColumnFilters.asReadonly();

  setPendingColumnFilters(model: FilterModel | null): void {
    this._pendingColumnFilters.set(model);
  }

  /**
   * Applies `model`, dropping the colIds this grid has no column for (a month
   * outside the picked window, a deleted consensus column) — AG Grid would only
   * warn and skip them. Returns the model that was actually applied.
   */
  applyColumnFilters(model: FilterModel): FilterModel {
    const api = this._gridApi;
    if (!api) return {};
    const applicable = Object.fromEntries(
      Object.entries(model).filter(([colId]) => !!api.getColumn(colId)),
    );
    // nothing this grid knows: leave its filters alone. Clearing here would
    // wipe the planner's own filters whenever a restore lands too early.
    if (Object.keys(applicable).length === 0) return {};
    api.setFilterModel(applicable);
    this.syncColumnFilterState();
    return applicable;
  }

  get gridApi(): GridApi | null {
    return this._gridApi;
  }

  registerGridApi(api: GridApi): void {
    this._gridApi = api;
    this._gridReady.set(true);
    this.syncColumnFilterState();
  }

  unregisterGridApi(): void {
    this._gridApi = null;
    this._gridReady.set(false);
    this._columnFilters.set({});
  }

  syncColumnFilterState(): void {
    this._columnFilters.set(
      (this._gridApi?.getFilterModel() ?? {}) as FilterModel,
    );
  }

  /**
   * Drops every column filter. AG Grid raises its own `filterChanged` from
   * this — and only when a filter was actually set — so the grid's handler
   * does the cache purge and refetch exactly once.
   */
  clearColumnFilters(): void {
    this._gridApi?.setFilterModel(null);
  }

  refreshServerSide(purge = true): void {
    this._gridApi?.refreshServerSide({ purge });
  }

  /**
   * Counts SSRM `getRows` fetches currently in flight. Used to coalesce
   * `refreshAllOpenBlocks` calls — see that method for the why.
   */
  private activeFetches = 0;

  /**
   * Set to true when a refresh was requested while at least one fetch was
   * in flight. Drained the moment the grid goes idle.
   */
  private refreshAfterIdle = false;

  /**
   * Set by a block refetch that stands in for a per-scope value reload; only
   * then does going idle release every pending scope. Any other fetch going
   * idle (scroll, expand) leaves a scope dimmed until its own reload lands.
   */
  private clearPendingAfterRefresh = false;

  private idleWaiters: (() => void)[] = [];

  /**
   * Called by the SSRM datasource at the start of every `getRows` call.
   * Pairs with {@link notifyFetchCompleted} in a try/finally.
   */
  notifyFetchStarted(): void {
    this.activeFetches += 1;
  }

  whenServerSideIdle(): Promise<void> {
    if (this.activeFetches === 0) return Promise.resolve();
    return new Promise((resolve) => this.idleWaiters.push(resolve));
  }

  private drainIdleWaiters(): void {
    if (this.idleWaiters.length === 0) return;
    const waiters = this.idleWaiters;
    this.idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /**
   * Called when a `getRows` call resolves (success or failure). If a
   * refresh was queued while fetches were busy, fires it now that the
   * grid is idle so AG Grid actually issues the GET instead of silently
   * coalescing it with the in-flight one.
   */
  notifyFetchCompleted(): void {
    this.activeFetches = Math.max(0, this.activeFetches - 1);
    if (this.activeFetches > 0) return;

    this.drainIdleWaiters();

    if (this.refreshAfterIdle) {
      this.refreshAfterIdle = false;
      this.fireRefreshAllOpenBlocks();
      return;
    }

    if (!this.clearPendingAfterRefresh) return;
    this.clearPendingAfterRefresh = false;

    // Fully idle, nothing queued: every open block has refetched since the
    // last COMPLETED, so the reconciled rows have all landed. Only now is it
    // safe to drop the dim state. Clearing it per-block (the old behaviour)
    // uncovered the stale value when the fast root block returned while the
    // slow children block carrying the edited rows was still in flight.
    this.clearPendingRefreshOnIdle();
  }

  /**
   * Reloads the values of the currently rendered rows inside the given
   * scopes. Registered by the grid component, which owns the row/level
   * bookkeeping. Returns false when it cannot serve the refresh — a value
   * filter or sort makes row membership depend on the values themselves, so
   * an edit can add or remove rows and only a block refetch is correct.
   */
  private renderedValueReloader: RenderedValueReloader | null = null;

  registerRenderedValueReloader(reloader: RenderedValueReloader): void {
    this.renderedValueReloader = reloader;
  }

  /**
   * Refresh after completed reconciliations. An edit changes cell values,
   * never which rows exist, so the row skeleton is left alone and only the
   * values of the rows in the completed scopes are reloaded; every other
   * scope keeps its spinner until its own completion lands.
   *
   * Falls back to refetching the blocks that hold affected rows when the
   * reloader declines (a value sort or filter makes row membership depend on
   * the values), and to nothing at all when no rendered row is affected.
   * The values path has to clear the dim state itself: that normally happens
   * when the grid goes idle after a refetch, and there is no refetch here.
   */
  async refreshAfterReconciliation(
    completed: readonly CompletedReconciliation[],
  ): Promise<void> {
    if (completed.length === 0) return;
    const months = [...new Set(completed.map((c) => c.dateValue))];
    const scopes = completed.map((c) => c.scope);
    if (!this.hasRenderedRowInScope(scopes)) {
      this.clearPendingRefreshScopes(completed);
      return;
    }
    const reloader = this.renderedValueReloader;
    const handled = reloader
      ? await reloader(months, scopes).catch(() => false)
      : false;

    if (!handled) {
      this.refreshBlocksMatchingScopes(scopes);
      return;
    }
    this.clearPendingRefreshScopes(completed);
  }

  /** True when at least one rendered row lies in one of the scopes. */
  hasRenderedRowInScope(scopes: readonly ReconciliationScope[]): boolean {
    return this.nodesMatchingScopes(scopes).length > 0;
  }

  private clearPendingRefreshScopes(
    completed: readonly CompletedReconciliation[],
  ): void {
    this.pendingRefreshScopesByColumnAndMonth.update((current) =>
      completed.reduce(
        (map, { scope, consensusColumnId, dateValue }) =>
          removeScope(map, consensusColumnId, dateValue, scope),
        current,
      ),
    );
    this.refreshRowsMatchingScopes(completed.map((c) => c.scope));
  }

  /**
   * Drops all pending-refresh scopes once the grid has gone idle after a
   * refresh wave, then repaints the affected cells so they reveal the
   * freshly-fetched values instead of staying dimmed.
   */
  private clearPendingRefreshOnIdle(): void {
    const pending = this.pendingRefreshScopesByColumnAndMonth();
    if (pending.size === 0) return;

    const scopes = extractScopes(pending);
    this.pendingRefreshScopesByColumnAndMonth.set(new Map());
    this.refreshRowsMatchingScopes(scopes);
  }

  /**
   * Refetches every currently-loaded SSRM block — the root block plus the
   * children block of each expanded row. AG Grid's `refreshServerSide`
   * without an explicit `route` only touches the root block, so children
   * blocks of expanded rows would otherwise stay stale until the user
   * collapses + re-expands. `purge: false` keeps scroll/expansion intact.
   *
   * Coalesces against in-flight fetches: AG Grid SSRM silently drops a
   * `refreshServerSide` while a previous refresh is still resolving, so rapid
   * bursts (e.g. several SSE COMPLETED events during a multi-month edit) used
   * to leave the last month at its pre-edit value until a reload. So instead
   * of firing now we mark "refresh once idle"; {@link notifyFetchCompleted}
   * then fires exactly one more refresh against the fully-applied backend
   * state — no hardcoded delays.
   */
  refreshAllOpenBlocks(): void {
    this.clearPendingAfterRefresh = true;
    if (this.activeFetches > 0) {
      this.refreshAfterIdle = true;
      return;
    }
    this.fireRefreshAllOpenBlocks();
  }

  private fireRefreshAllOpenBlocks(): void {
    const api = this._gridApi;
    if (!api) return;
    api.refreshServerSide({ route: [], purge: false });
    api.forEachNode((node) => {
      if (node.expanded && node.id !== null && node.level >= 0) {
        api.refreshServerSide({ route: routeOf(node), purge: false });
      }
    });
  }

  /**
   * Refetches only the SSRM blocks that hold a row inside one of the scopes:
   * a value sort or filter can move or drop those rows, every other block is
   * untouched by the edit. Defers to the coalesced full refresh while fetches
   * are in flight, since AG Grid drops refreshes that overlap one.
   */
  refreshBlocksMatchingScopes(scopes: readonly ReconciliationScope[]): void {
    const api = this._gridApi;
    if (!api) return;
    if (this.activeFetches > 0) {
      this.refreshAllOpenBlocks();
      return;
    }
    const routes = new Map<string, string[]>();
    for (const node of this.nodesMatchingScopes(scopes)) {
      const route = node.parent ? routeOf(node.parent) : [];
      routes.set(route.join('/'), route);
    }
    if (routes.size === 0) return;
    this.clearPendingAfterRefresh = true;
    for (const route of routes.values()) {
      api.refreshServerSide({ route, purge: false });
    }
  }

  refreshCells(params?: RefreshCellsParams): void {
    this._gridApi?.refreshCells(params);
  }

  setLoading(loading: boolean): void {
    this._gridApi?.setGridOption('loading', loading);
  }

  addReconcilingScope(
    scope: ReconciliationScope,
    consensusColumnId: string,
    dateValue: string,
  ): void {
    this.reconcilingScopesByColumnAndMonth.update((current) =>
      upsertScope(current, consensusColumnId, dateValue, scope),
    );
    this.refreshRowsMatchingScopes([scope]);
  }

  /** Ends a reconciliation that no longer touches this grid: undims, no refresh. */
  dropReconcilingScope(
    scope: ReconciliationScope,
    consensusColumnId: string,
    dateValue: string,
  ): void {
    this.reconcilingScopesByColumnAndMonth.update((current) =>
      removeScope(current, consensusColumnId, dateValue, scope),
    );
    this.refreshRowsMatchingScopes([scope]);
  }

  /**
   * Called on SSE COMPLETED — moves the scope from `reconciling` to
   * `pendingRefresh`.
   */
  markScopeCompletedAwaitingRefresh(
    scope: ReconciliationScope,
    consensusColumnId: string,
    dateValue: string,
  ): void {
    this.reconcilingScopesByColumnAndMonth.update((current) =>
      removeScope(current, consensusColumnId, dateValue, scope),
    );
    this.pendingRefreshScopesByColumnAndMonth.update((current) =>
      upsertScope(current, consensusColumnId, dateValue, scope),
    );
    this.refreshRowsMatchingScopes([scope]);
  }

  syncReconcilingFromSnapshot(
    snapshot: Record<string, Record<string, readonly ReconciliationScope[]>>,
  ): void {
    const previous = this.reconcilingScopesByColumnAndMonth();
    const next = buildScopeMap(snapshot);

    // collect every scope that was reconciling before OR is reconciling
    // after — those rows need a repaint in this paint. We deliberately do
    // NOT clear pendingRefresh here: this runs once per SSRM block, and the
    // fast root block would otherwise uncover the slow children block's
    // still-stale rows. pendingRefresh is cleared in notifyFetchCompleted
    // once the whole refresh wave has gone idle (see clearPendingRefreshOnIdle).
    const scopesToRefresh = [
      ...extractScopes(previous),
      ...extractScopes(next),
      ...extractScopes(this.pendingRefreshScopesByColumnAndMonth()),
    ];

    this.reconcilingScopesByColumnAndMonth.set(next);
    this.refreshRowsMatchingScopes(scopesToRefresh);
  }

  private refreshRowsMatchingScopes(
    scopes: readonly ReconciliationScope[],
  ): void {
    const nodes = this.nodesMatchingScopes(scopes);
    if (nodes.length === 0) return;
    this._gridApi?.refreshCells({ rowNodes: nodes, force: true });
  }

  private nodesMatchingScopes(
    scopes: readonly ReconciliationScope[],
  ): IRowNode[] {
    const api = this._gridApi;
    if (!api || scopes.length === 0) return [];

    const nodes: IRowNode[] = [];
    api.forEachNode((node) => {
      const data = node.data as
        | { dimensions?: Record<string, FilterValue> }
        | undefined;
      if (!data?.dimensions) return;
      const row = { dimensions: data.dimensions };
      if (scopes.some((s) => rowMatchesScope(row, s))) {
        nodes.push(node);
      }
    });
    return nodes;
  }
}

/** The SSRM route of a node: the ids from the first level down to it. */
function routeOf(node: IRowNode): string[] {
  const route: string[] = [];
  let current: IRowNode | null = node;
  while (current?.id != null && current.level >= 0) {
    route.unshift(current.id);
    current = current.parent;
  }
  return route;
}
